import { forwardRef, ForwardRefRenderFunction, useState } from "react";
//#region types
import { BenefitsEnums } from "models/enums";
import { BenefitFormTypes } from "models/formTypes";
import { BenefitCreateFormProps } from "models/propTypes/benefitsPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { ChoiceProps, MultiSearchInputProps, SearchInputProps } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import {
  Choice,
  MultiSearchInput,
  SearchInput,
  useWintForm,
  VerticalFormField,
  WintForm,
} from "components/common/wintForms";
import BenefitName from "./benefitName";
import BenefitMonthsSelector from "./benefitMonthsSelector";
import CarBenefitSection from "../_partials/carBenefitSection";
import BenefitValue from "../_partials/benefitValue";
import { getDefaultBenefitFrequency, isDeductionBenefitType, withDeductionWording } from "./useBenefitTypesHelper";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const BenefitCreateForm: ForwardRefRenderFunction<FormHandles, BenefitCreateFormProps> = (props, ref) => {
  const {
    defaultValues,
    validationSchema,
    handleSubmit,
    frequencyChoices,
    benefitTypes,
    employees,
    yearList,
    brandList,
    onFormUpdate,
  } = props;

  const { wintFormProps, connect, values, setValues } = useWintForm<BenefitFormTypes.BenefitCreate>({
    formRef: ref,
    handleSubmit: handleSubmit,
    defaultValues: defaultValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onFormUpdate: onFormUpdate,
  });

  const [isCalculatingCarBenefit, setIsCalculatingCarBenefit] = useState(false);

  const benefitTypeConnect = connect("benefitType");
  const benefitTypeId = values.benefitType?.id;

  const isDeduction = isDeductionBenefitType(benefitTypeId);

  const isCarBenefit =
    values.benefitType?.id === BenefitsEnums.BenefitType.Car ||
    values.benefitType?.id === BenefitsEnums.BenefitType.CarNetDeduction;

  const handleBenefitTypeChange = (newValue: any, shouldValidate?: boolean) => {
    setValues(
      {
        benefitType: newValue,
        frequency: getDefaultBenefitFrequency(newValue?.id),
        carBenefitData: {
          searchMethod: BenefitsEnums.CarBenefitSearchMethod.ModelNumber,
          isAnnual10KmMileageMoreThan3000: false,
        },
      },
      shouldValidate
    );
  };

  return (
    <WintForm {...wintFormProps}>
      <VerticalFormField<SearchInputProps>
        Component={SearchInput}
        {...benefitTypeConnect}
        setValue={handleBenefitTypeChange}
        label={TranslationKeys.Benefit.BenefitTypeLabel}
        items={benefitTypes}
        descriptionText={benefitTypeId !== undefined ? `Benefit.TypeDescription_${benefitTypeId}` : undefined}
      />
      <VerticalFormField<MultiSearchInputProps>
        Component={MultiSearchInput}
        {...connect("employees")}
        label={TranslationKeys.Common.Employees}
        includeSelectAll={isCarBenefit ? undefined : true}
        selectAllIcon={IconDefinitionConstants.WUser}
        forceSingleSelection={isCarBenefit ? true : undefined}
        items={employees}
      />
      <BenefitName connect={connect} benefitTypeName={values.benefitType?.value} benefitTypeId={benefitTypeId} />
      {!isCarBenefit && (
        <VerticalFormField<ChoiceProps>
          Component={Choice}
          {...connect("frequency")}
          label={TranslationKeys.Benefit.FrequencyLabel}
          items={isDeduction ? withDeductionWording(frequencyChoices) : frequencyChoices}
          showMainLabel
        />
      )}
      <BenefitMonthsSelector
        connect={connect}
        frequency={isCarBenefit ? BenefitsEnums.BenefitFrequency.Monthly : values.frequency}
        mainLabel={
          isCarBenefit ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Period) : undefined
        }
      />
      {isCarBenefit && (
        <CarBenefitSection
          connect={connect}
          yearList={yearList}
          brandList={brandList}
          setValues={setValues}
          values={values}
          setIsCalculating={setIsCalculatingCarBenefit}
        />
      )}
      <BenefitValue
        connect={connect}
        benefitTypeId={benefitTypeId}
        isCarBenefit={isCarBenefit}
        isCalculating={isCalculatingCarBenefit}
      />
    </WintForm>
  );
};

export default forwardRef(BenefitCreateForm);
